import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { 
  Smartphone, 
  Tablet, 
  Monitor, 
  AlertCircle, 
  AlertTriangle, 
  CheckCircle2, 
  Image, 
  Link2, 
  FileText, 
  Code, 
  Search,
  Share2,
  Globe,
  Lock,
  Heading1,
  Tag
} from 'lucide-react';

// Score color helper
const getScoreColor = (score) => {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
};

const getScoreBg = (score) => {
  if (score >= 90) return 'bg-green-100 border-green-300';
  if (score >= 70) return 'bg-yellow-100 border-yellow-300';
  if (score >= 50) return 'bg-orange-100 border-orange-300';
  return 'bg-red-100 border-red-300';
};

const getStatusIcon = (status) => {
  switch(status) {
    case 'good':
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case 'needs_work':
      return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    case 'poor':
      return <AlertCircle className="h-5 w-5 text-red-600" />;
    default:
      return <AlertCircle className="h-5 w-5 text-gray-400" />;
  }
};

const getStatusColor = (status) => {
  switch(status) {
    case 'good': return 'text-green-600 bg-green-50';
    case 'needs_work': return 'text-yellow-600 bg-yellow-50';
    case 'poor': return 'text-red-600 bg-red-50';
    default: return 'text-gray-600 bg-gray-50';
  }
};

// Responsive Design Analysis Component
export const ResponsiveDesignAnalysis = ({ data }) => {
  if (!data) return null;

  const { score, mobile, tablet, desktop, critical_issues, warnings, passes } = data;

  return (
    <Card className="border-2 border-cyan-300" id="responsive-detail">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-6 w-6 text-cyan-600" />
          Responsive Design Analysis
        </CardTitle>
        <CardDescription>How your website performs across mobile, tablet, and desktop devices</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Score */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200">
          <div>
            <p className="text-sm font-medium text-slate-600">Responsive Score</p>
            <p className="text-xs text-slate-500 mt-1">Based on viewport, media queries, and mobile optimization</p>
          </div>
          <div className={`text-4xl font-bold ${getScoreColor(score)}`}>
            {score}/100
          </div>
        </div>

        {/* Device Status Grid */}
        <div className="grid grid-cols-3 gap-4">
          {/* Mobile */}
          <div className={`p-4 rounded-lg border-2 ${getStatusColor(mobile?.status)} text-center`}>
            <Smartphone className="h-8 w-8 mx-auto mb-2 opacity-70" />
            <p className="font-semibold">Mobile</p>
            <div className="flex items-center justify-center gap-1 mt-2">
              {getStatusIcon(mobile?.status)}
              <span className="capitalize text-sm">{mobile?.status || 'Unknown'}</span>
            </div>
          </div>

          {/* Tablet */}
          <div className={`p-4 rounded-lg border-2 ${getStatusColor(tablet?.status)} text-center`}>
            <Tablet className="h-8 w-8 mx-auto mb-2 opacity-70" />
            <p className="font-semibold">Tablet</p>
            <div className="flex items-center justify-center gap-1 mt-2">
              {getStatusIcon(tablet?.status)}
              <span className="capitalize text-sm">{tablet?.status || 'Unknown'}</span>
            </div>
          </div>

          {/* Desktop */}
          <div className={`p-4 rounded-lg border-2 ${getStatusColor(desktop?.status)} text-center`}>
            <Monitor className="h-8 w-8 mx-auto mb-2 opacity-70" />
            <p className="font-semibold">Desktop</p>
            <div className="flex items-center justify-center gap-1 mt-2">
              {getStatusIcon(desktop?.status)}
              <span className="capitalize text-sm">{desktop?.status || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Critical Issues */}
        {critical_issues && critical_issues.length > 0 && (
          <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
            <h4 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5" />
              Critical Issues ({critical_issues.length})
            </h4>
            <ul className="space-y-2">
              {critical_issues.map((issue, idx) => (
                <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
            <h4 className="font-semibold text-yellow-800 flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5" />
              Warnings ({warnings.length})
            </h4>
            <ul className="space-y-2">
              {warnings.map((warning, idx) => (
                <li key={idx} className="text-sm text-yellow-700 flex items-start gap-2">
                  <span className="text-yellow-500 mt-0.5">•</span>
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Passes */}
        {passes && passes.length > 0 && (
          <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
            <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5" />
              Passing Checks ({passes.length})
            </h4>
            <ul className="space-y-2">
              {passes.map((pass, idx) => (
                <li key={idx} className="text-sm text-green-700 flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">•</span>
                  <span>{pass}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// SEO Technical Analysis Component
export const SEOTechnicalAnalysis = ({ data }) => {
  if (!data) return null;

  const { 
    score, 
    meta_tags, 
    headings, 
    images, 
    links, 
    structured_data, 
    technical,
    critical_issues, 
    warnings, 
    passes 
  } = data;

  return (
    <Card className="border-2 border-blue-300" id="seo-technical-detail">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-6 w-6 text-blue-600" />
          SEO Technical Analysis
        </CardTitle>
        <CardDescription>Detailed analysis of on-page SEO elements and technical factors</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Score */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-blue-50 to-slate-50 border-2 border-blue-200">
          <div>
            <p className="text-sm font-medium text-slate-600">SEO Technical Score</p>
            <p className="text-xs text-slate-500 mt-1">Based on meta tags, headings, images, and structured data</p>
          </div>
          <div className={`text-4xl font-bold ${getScoreColor(score)}`}>
            {score}/100
          </div>
        </div>

        {/* Meta Tags Section */}
        {meta_tags && (
          <div className="p-4 bg-slate-50 rounded-lg border">
            <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <Tag className="h-5 w-5 text-blue-500" />
              Meta Tags
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-start">
                <span className="text-slate-600 font-medium">Title:</span>
                <span className={`max-w-xs truncate ${meta_tags.title ? 'text-green-600' : 'text-red-600'}`}>
                  {meta_tags.title || '❌ Missing'}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-slate-600 font-medium">Description:</span>
                <span className={`max-w-xs truncate ${meta_tags.description ? 'text-green-600' : 'text-red-600'}`}>
                  {meta_tags.description ? `${meta_tags.description.substring(0, 50)}...` : '❌ Missing'}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-slate-600 font-medium">Canonical URL:</span>
                <span className={meta_tags.canonical ? 'text-green-600' : 'text-yellow-600'}>
                  {meta_tags.canonical ? '✅ Set' : '⚠️ Missing'}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-slate-600 font-medium">Robots:</span>
                <span className="text-slate-700">{meta_tags.robots || 'Default (index, follow)'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Headings & Images Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Headings */}
          {headings && (
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Heading1 className="h-5 w-5 text-blue-500" />
                Heading Structure
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">H1 Tags:</span>
                  <span className={headings.h1_count === 1 ? 'text-green-600 font-medium' : 'text-yellow-600 font-medium'}>
                    {headings.h1_count} {headings.h1_count === 1 ? '✓' : headings.h1_count === 0 ? '❌' : '⚠️'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">H2 Tags:</span>
                  <span className="text-slate-700">{headings.h2_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">H3 Tags:</span>
                  <span className="text-slate-700">{headings.h3_count || 0}</span>
                </div>
                {headings.h1_texts && headings.h1_texts.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-slate-500">H1 Content:</p>
                    <p className="text-xs text-slate-700 truncate">{headings.h1_texts[0]}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Images */}
          {images && (
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Image className="h-5 w-5 text-green-500" />
                Image Alt Tags
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Images:</span>
                  <span className="text-slate-700">{images.total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">With Alt Tags:</span>
                  <span className="text-green-600 font-medium">{images.with_alt || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Missing Alt Tags:</span>
                  <span className={images.missing_alt > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                    {images.missing_alt || 0} {images.missing_alt > 0 ? '❌' : '✓'}
                  </span>
                </div>
                {images.total > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${((images.with_alt || 0) / images.total) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 text-center">
                      {Math.round(((images.with_alt || 0) / images.total) * 100)}% complete
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Links & Structured Data Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Links */}
          {links && (
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Link2 className="h-5 w-5 text-indigo-500" />
                Links Analysis
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Links:</span>
                  <span className="text-slate-700">{links.total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Internal Links:</span>
                  <span className="text-blue-600">{links.internal || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">External Links:</span>
                  <span className="text-blue-600">{links.external || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Nofollow Links:</span>
                  <span className="text-slate-700">{links.nofollow || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Structured Data */}
          {structured_data && (
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Code className="h-5 w-5 text-orange-500" />
                Structured Data (Schema.org)
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Schema Found:</span>
                  <span className={structured_data.count > 0 ? 'text-green-600 font-medium' : 'text-yellow-600'}>
                    {structured_data.count > 0 ? `✅ ${structured_data.count} found` : '⚠️ None'}
                  </span>
                </div>
                {structured_data.types && structured_data.types.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 mb-1">Schema Types:</p>
                    <div className="flex flex-wrap gap-1">
                      {structured_data.types.slice(0, 5).map((type, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Social & Technical */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Open Graph */}
          {meta_tags?.open_graph && (
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Share2 className="h-5 w-5 text-blue-500" />
                Open Graph (Social)
              </h4>
              <div className="space-y-2 text-sm">
                {Object.keys(meta_tags.open_graph).length > 0 ? (
                  Object.entries(meta_tags.open_graph).slice(0, 4).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-slate-600">{key.replace('og:', '')}:</span>
                      <span className="text-green-600 truncate max-w-[150px]">✅ Set</span>
                    </div>
                  ))
                ) : (
                  <p className="text-yellow-600">⚠️ No Open Graph tags found</p>
                )}
              </div>
            </div>
          )}

          {/* Technical */}
          {technical && (
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Globe className="h-5 w-5 text-teal-500" />
                Technical
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">HTTPS/SSL:</span>
                  <span className={technical.ssl ? 'text-green-600' : 'text-red-600'}>
                    {technical.ssl ? '✅ Secure' : '❌ Not Secure'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Language:</span>
                  <span className={technical.language ? 'text-green-600' : 'text-yellow-600'}>
                    {technical.language || '⚠️ Not set'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Critical Issues */}
        {critical_issues && critical_issues.length > 0 && (
          <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
            <h4 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5" />
              Critical SEO Issues ({critical_issues.length})
            </h4>
            <ul className="space-y-2">
              {critical_issues.map((issue, idx) => (
                <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
            <h4 className="font-semibold text-yellow-800 flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5" />
              SEO Warnings ({warnings.length})
            </h4>
            <ul className="space-y-2">
              {warnings.map((warning, idx) => (
                <li key={idx} className="text-sm text-yellow-700 flex items-start gap-2">
                  <span className="text-yellow-500 mt-0.5">•</span>
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Passes */}
        {passes && passes.length > 0 && (
          <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
            <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5" />
              Passing SEO Checks ({passes.length})
            </h4>
            <ul className="space-y-2">
              {passes.map((pass, idx) => (
                <li key={idx} className="text-sm text-green-700 flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">•</span>
                  <span>{pass}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Priority Fixes Component
export const PriorityFixes = ({ fixes }) => {
  if (!fixes || fixes.length === 0) return null;

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-300';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <Card className="border-2 border-red-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-6 w-6 text-red-600" />
          Priority Fixes
        </CardTitle>
        <CardDescription>Top issues to fix for better performance and SEO</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {fixes.map((fix, idx) => (
            <div 
              key={idx} 
              className={`p-3 rounded-lg border-2 ${getSeverityColor(fix.severity)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      fix.severity === 'CRITICAL' ? 'bg-red-600 text-white' : 'bg-orange-600 text-white'
                    }`}>
                      {fix.severity}
                    </span>
                    <span className="text-xs text-slate-500">{fix.category}</span>
                  </div>
                  <p className="text-sm font-medium">{fix.issue}</p>
                  {fix.impact && (
                    <p className="text-xs text-slate-600 mt-1">Impact: {fix.impact}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default { ResponsiveDesignAnalysis, SEOTechnicalAnalysis, PriorityFixes };
